export interface ScheduleStateModel {
  schedule: ScheduleResponse[];
  weeklySchedules: WeeklyScheduleModel[];
  scheduleExports: any[];
  loading: LoadingScheduleState;
  error: any | null;
  validationReport: ValidationReport | null;
  aiStatus: AIStatus | null;
  /**
   * Result of the most recent learner schedule transfer.
   * Résultat du dernier transfert d'apprenant entre horaires.
   */
  lastTransferResult: TransferLearnerResult | null;
  /**
   * Learners enrolled in the schedule selected as the transfer source.
   * Apprenants inscrits dans l'horaire sélectionné comme source du transfert.
   */
  scheduleLearners: WeeklyScheduleLearnerModel[];
}

export interface ScheduleResponse {
  id?: string;
  trackName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WeeklyScheduleModel {
  id?: string;
  name?: string;
  schedules?: SingleScheduleModel[];
}
export interface SingleScheduleModel {
  id: string
  day: Weekdays
  period: "AM" | "PM" | "Evening"
}

export interface ScheduleRequest {
  day: Weekdays
  periods: string[]
  learningId: string
}

export interface WeeklyScheduleRequest {
  id: string,
  name: string,
  learningIds: string[],
  scheduleIds: string[],
}

export interface ScheduleByIdSessionRequest {
  id: string,
  sessionId: string
}

export enum LoadingScheduleState {
  loadingList = 'loadingList',
  loadingAddUpdate = 'loadingAddUpdate',
  loadingDelete = 'loadingDelete',
  notLoading = 'notLoading',
}

type Weekdays = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';

// ============================================================================
// NEW INTERFACES FOR ALGORITHM UPDATE & AI/ML
// ============================================================================

export interface GenerateTracksRequest {
  id: string;  // weeklyScheduleId
  twoCaseTrackCount?: number;
  threeCaseTrackCount?: number;
  enableAI?: boolean;
  autoValidate?: boolean;
}

export interface GenerateScheduleResponse {
  success: boolean;
  message: string;
  tracksGenerated?: number;
  validationReport?: ValidationReport;
}

export interface ValidationReport {
  success: boolean;
  warnings: string[];
  errors: string[];
  statistics: ValidationStatistics;
  learnerDetails: LearnerDetail[];
  aiInsights?: AIInsights;
  generatedAt: Date;
}

export interface ValidationStatistics {
  totalLearners: number;
  totalSessions: number;
  averageLeadershipsPerLearner: number;
  balanceScore: number;
}

export interface LearnerDetail {
  learnerId: string;
  learnerName: string;
  totalLeaderships: number;
  twoCaseLeaderships: number;
  threeCaseLeaderships: number;
}

export interface AIInsights {
  confidence: number;
  suggestions: string[];
}

export interface AIStatus {
  realDataPoints: number;
  modelVersion: string;
  modelType: 'rules' | 'forecast' | 'xgboost' | 'ensemble';
  lastTrainedAt: Date;
  performance: {
    accuracy: number;
    degradation: number;
  };
  confidence: number;
}

export interface AddUserToScheduleRequest {
  weeklyScheduleId: string;
  sessionId: string;
  userId: string;
  trackName?: string;
  groupName?: string;
  placementMode?: 'auto' | 'manual' | 'balanced';
}

/** Transfer a learner from one weekly schedule to another / Transférer un apprenant entre horaires */
export interface TransferLearnerRequest {
  applicationId: string;
  fromWeeklyScheduleId: string;
  toWeeklyScheduleId: string;
}

export interface TransferLearnerResult {
  learner: { userId: string; name: string; applicationId: string };
  fromWeeklyScheduleId: string;
  toWeeklyScheduleId: string;
  sessionsTransferred: number;
  leaderSwaps: Array<{ fromUserId: string; toUserId: string; sessionId: string; caseId: string }>;
  repairConflicts: string[];
  repairConflictsFr: string[];
}

/**
 * A learner record returned by GET /schedules/weekly/:id/learners.
 * Represents one unique person enrolled in a weekly schedule.
 *
 * Enregistrement d'un apprenant retourné par GET /schedules/weekly/:id/learners.
 * Représente une personne unique inscrite dans un horaire hebdomadaire.
 */
export interface WeeklyScheduleLearnerModel {
  userId: string;
  applicationId: string;
  firstName: string;
  lastName: string;
  userName: string;
  PGYLevel: string;
  postgraduateTrainingProgram: string;
  associatedMedicalSchool: string;
}


